import { Box, Center, Divider, Flex, Icon, IconButton, Spinner, Text } from '@chakra-ui/react'
import { DotsHorizontal, Plus, TagFilled, TrashOutline } from '../../../utils/constants/icons'
import LazyLoadAvatar from '../media/LazyLoadedAvatar'

interface OptionListProps {
  options: {
    id: string
    name: string
    image?: string | null
    color?: string
    detail?: string
    disabled?: boolean
    disabledReason?: string
  }[]
  onOptionSelect: (option: { id: string; name: string; image?: string | null; color?: string }) => void
  selectedOption?: string
  noImage?: boolean
  isTag?: boolean
  isPdf?: boolean
  textAlign?: 'start' | 'center' | 'right'
  isLoading?: boolean
  searchValue?: string
  showAllOptions?: boolean
  hasAddButton?: boolean
  onAdd?: (name: string) => void
  itemName?: string
  isAddLoading?: boolean
  hasRawValueOption?: boolean
  hasNoValueSet?: boolean
  hasSetValueNull?: boolean
  onDelete?: (id: string) => void
  getAvatar?: (value: { id: string; name: string; image?: string | null; color?: string }) => React.ReactNode
}

export const OptionItem = ({
  option,
  isActive,
  noImage,
  isTag,
  isPdf,
  textAlign = 'start',
  onClick,
  onDelete,
  getAvatar,
}: {
  isActive?: boolean
  option: {
    id: string
    name: string
    image?: string | null
    color?: string
    detail?: string
    disabled?: boolean
    disabledReason?: string
  }
  noImage?: boolean
  isTag?: boolean
  isPdf?: boolean
  textAlign?: 'start' | 'center' | 'right'
  onClick: () => void
  onDelete?: () => void
  getAvatar?: (value: { id: string; name: string; image?: string | null; color?: string }) => React.ReactNode
}) => {
  return (
    <Flex
      px="16px"
      h="48px"
      cursor={option.disabled ? 'not-allowed' : 'pointer'}
      alignItems="center"
      transition="0.2s ease-out"
      justifyContent="flex-start"
      py="8px"
      _hover={{ bg: option.disabled ? 'transparent' : 'neutral.50' }}
      bg={isActive ? 'neutral.50' : 'transparent'}
      onClick={option.disabled ? undefined : onClick}
      opacity={option.disabled ? 0.5 : 1}
    >
      {!noImage && !isTag && !isPdf && (getAvatar ? getAvatar(option) : <LazyLoadAvatar name={option.name} w={32} h={32} src={option.image} isImage />)}
      {isTag && (
        <Center w="32px" h="32px" borderRadius="50%" bg={option?.color ?? 'neutral.100'}>
          <Icon w="18px" h="18px" viewBox="0 0 20 20" fill="none" color="white">
            <TagFilled />
          </Icon>
        </Center>
      )}
      {isPdf && (
        <Box w="20px" h="20px" bgImage={'/icons/pdf.svg'} bgSize="contain" bgRepeat="no-repeat" bgPosition="center" />
      )}
      <Box ml="8px" textAlign={textAlign} flex="1">
        <Text textAlign={textAlign} noOfLines={1} textStyle="b-md" color="neutral.900">
          {option.name}
        </Text>
        {option.detail && (
          <Text textAlign={textAlign} noOfLines={1} textStyle="b-xs" color="neutral.300">
            {option.detail}
          </Text>
        )}
        {option.disabled && option.disabledReason && (
          <Text textAlign={textAlign} noOfLines={1} textStyle="b-xs" color="neutral.300">
            {option.disabledReason}
          </Text>
        )}
      </Box>
      {onDelete && (
        <Box ml="auto">
          <IconButton
            cursor="pointer"
            aria-label="Delete option"
            icon={
              <Icon w="16px" h="16px" fill="none">
                <TrashOutline />
              </Icon>
            }
            size="sm"
            variant="unstyled"
            _hover={{ color: 'red.500' }}
            transition="0.2s ease-out"
            color="neutral.400"
            onClick={onDelete}
          />
        </Box>
      )}
    </Flex>
  )
}

export const OptionList = ({
  options,
  onOptionSelect,
  selectedOption,
  noImage,
  isTag,
  isPdf,
  textAlign = 'start',
  isLoading,
  searchValue = '',
  showAllOptions,
  hasAddButton,
  onAdd,
  itemName,
  isAddLoading,
  hasRawValueOption,
  hasNoValueSet,
  hasSetValueNull,
  onDelete,
  getAvatar,
}: OptionListProps) => {
  return (
    <>
      {options?.length === 0 && (
        <Flex h="64px" p="2px 16px" alignItems="center">
          <Text w="100%" textAlign={'center'} textStyle="b-sm" color="neutral.500">
            {isLoading && showAllOptions
              ? 'Loading options...'
              : searchValue.length === 0
              ? 'Start typing to search...'
              : 'No results found'}
          </Text>
        </Flex>
      )}

      {options?.map((option, i) => (
        <Box key={option.id}>
          <OptionItem
            isActive={selectedOption === option.id}
            option={option}
            noImage={noImage}
            isTag={isTag}
            isPdf={isPdf}
            textAlign={textAlign}
            onClick={() => onOptionSelect(option)}
            onDelete={onDelete ? () => onDelete?.(option.id) : undefined}
            getAvatar={getAvatar}
          />
          {i !== options.length - 1 && <Divider m="0" />}
        </Box>
      ))}

      {hasAddButton && onAdd && searchValue.length > 0 && (
        <>
          <Divider m="0" />
          <Flex
            px="16px"
            h="48px"
            cursor="pointer"
            alignItems="center"
            transition="0.2s ease-out"
            justifyContent="flex-start"
            py="8px"
            _hover={{ bg: 'neutral.50' }}
            opacity={isAddLoading ? 0.6 : 1}
            onClick={() => {
              if (!isAddLoading && onAdd) {
                onAdd(searchValue)
              }
            }}
          >
            {isAddLoading ? (
              <Spinner size="xs" color="primary.500" mr="4px" />
            ) : (
              <Center w="32px" h="32px" borderWidth="1px" borderColor="neutral.100" borderRadius="50%">
                <Icon w="24px" h="24px" viewBox="0 0 24 24" fill="none" color="primary.500">
                  <Plus />
                </Icon>
              </Center>
            )}
            <Text ml="8px" noOfLines={1} textStyle="b-md" color="neutral.900">
              Create a new {itemName ?? 'item'}
            </Text>
          </Flex>
        </>
      )}

      {hasRawValueOption && !isLoading && (
        <>
          <Divider m="0" />
          <Flex
            px="16px"
            h="48px"
            cursor="pointer"
            alignItems="center"
            transition="0.2s ease-out"
            justifyContent="flex-start"
            py="8px"
            _hover={{ bg: 'neutral.50' }}
            onClick={() => {
              onOptionSelect({
                id: 'RAW_VALUE',
                name: searchValue,
              })
            }}
          >
            <Center w="32px" h="32px" borderWidth="1px" borderColor="neutral.100" borderRadius="50%">
              <Icon w="16px" h="16px" viewBox="0 0 24 24" fill="none" color="primary.500">
                <DotsHorizontal />
              </Icon>
            </Center>
            <Box ml="8px">
              <Text noOfLines={1} textStyle="b-md" color="neutral.900">
                {searchValue}
              </Text>
              <Text noOfLines={1} textStyle="b-xs" color="neutral.300">
                Find items with unlinked product type
              </Text>
            </Box>
          </Flex>
        </>
      )}

      {hasNoValueSet && (
        <>
          <Divider m="0" />
          <Flex
            px="16px"
            h="48px"
            cursor="pointer"
            alignItems="center"
            transition="0.2s ease-out"
            justifyContent="flex-start"
            py="8px"
            _hover={{ bg: 'neutral.50' }}
            onClick={() => {
              onOptionSelect({
                id: 'NO_VALUE_SET',
                name: 'NO_VALUE_SET',
              })
            }}
          >
            <Center w="32px" h="32px" borderWidth="1px" borderColor="neutral.100" borderRadius="50%">
              <Icon w="16px" h="16px" viewBox="0 0 24 24" fill="none" color="primary.500">
                <DotsHorizontal />
              </Icon>
            </Center>
            <Box ml="8px">
              <Text noOfLines={1} textStyle="b-md" color="neutral.900">
                No value set
              </Text>
              <Text noOfLines={1} textStyle="b-xs" color="neutral.300">
                Select values with no value set
              </Text>
            </Box>
          </Flex>
        </>
      )}

      {hasSetValueNull && (
        <>
          <Divider m="0" />
          <Flex
            px="16px"
            h="48px"
            cursor="pointer"
            alignItems="center"
            transition="0.2s ease-out"
            justifyContent="flex-start"
            py="8px"
            _hover={{ bg: 'neutral.50' }}
            onClick={() => {
              onOptionSelect({
                id: 'NULL_VALUE',
                name: 'NULL_VALUE',
              })
            }}
          >
            <Center w="32px" h="32px" borderWidth="1px" borderColor="neutral.100" borderRadius="50%">
              <Icon w="16px" h="16px" viewBox="0 0 24 24" fill="none" color="primary.500">
                <DotsHorizontal />
              </Icon>
            </Center>
            <Box ml="8px">
              <Text noOfLines={1} textStyle="b-md" color="neutral.900">
                No value set
              </Text>
              <Text noOfLines={1} textStyle="b-xs" color="neutral.300">
                Set the value to null
              </Text>
            </Box>
          </Flex>
        </>
      )}
    </>
  )
}

export default OptionList
